import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, TrendingUp, MousePointerClick, Star, LogOut, Activity, Tag, Boxes, History, ThumbsUp, Share2, Award, ShieldCheck, AlertTriangle, ShoppingBag, RotateCcw, X } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid } from "recharts";
import { Header } from "../components/Header";
import { AdminNav } from "../components/AdminNav";
import { fetchAdminStats, fetchAdminSearches, resetDevMetrics } from "../lib/api";
import { ADMIN } from "../constants/testIds";

const StatCard = ({ icon: Icon, label, value, n, color = "emerald" }) => (
  <div data-testid={ADMIN.statCard(n)} className="bg-card border border-border rounded-2xl p-5">
    <div className="flex items-center justify-between mb-3">
      <span className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</span>
      <div className={`w-8 h-8 rounded-lg bg-${color}-500/15 flex items-center justify-center`}>
        <Icon className={`w-4 h-4 text-${color}-500`} />
      </div>
    </div>
    <div className="font-display font-bold text-3xl tracking-tight">{value}</div>
  </div>
);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [searches, setSearches] = useState([]);
  const [err, setErr] = useState("");
  const [resetOpen, setResetOpen] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [resetConfirm, setResetConfirm] = useState("");
  const [includeClicks, setIncludeClicks] = useState(true);
  const [resetMsg, setResetMsg] = useState("");

  const loadAll = () => {
    Promise.all([fetchAdminStats(), fetchAdminSearches()])
      .then(([s, ss]) => {
        setStats(s);
        setSearches(ss);
      })
      .catch((e) => {
        if (e?.response?.status === 401) {
          localStorage.removeItem("sbi_admin_token");
          navigate("/admin");
        } else {
          setErr(e?.message || "Failed to load");
        }
      });
  };

  useEffect(() => {
    if (!localStorage.getItem("sbi_admin_token")) {
      navigate("/admin");
      return;
    }
    loadAll();
  }, [navigate]);

  const runReset = async () => {
    if (resetConfirm !== "RESET") return;
    setResetBusy(true);
    setResetMsg("");
    try {
      const r = await resetDevMetrics({ includeAffiliateClicks: includeClicks });
      setResetMsg(
        `Reset complete. ${r.searches_reset} searches cleared · ${r.votes_deleted} votes deleted${
          includeClicks ? ` · ${r.affiliate_clicks_deleted} clicks deleted` : ""
        }.`,
      );
      setResetConfirm("");
      setResetOpen(false);
      loadAll();
    } catch (e) {
      setResetMsg(`Failed: ${e?.response?.data?.detail || e?.message || "unknown error"}`);
    } finally {
      setResetBusy(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("sbi_admin_token");
    localStorage.removeItem("sbi_admin_email");
    navigate("/admin");
  };

  if (err) return <div className="p-10 text-red-400">{err}</div>;
  if (!stats) return <div className="min-h-screen bg-background"><Header /><div className="p-10 text-muted-foreground">Loading…</div></div>;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-7xl mx-auto px-5 py-8">
        <AdminNav onLogout={logout} />

        <div className="mb-5">
          <h1 className="font-display font-bold text-2xl tracking-tight">Overview</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Real-time analytics for ShouldIBuyIt.co.uk</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard icon={Search} label="Total searches" value={stats.total_searches} n="searches" />
          <StatCard icon={Activity} label="Chat sessions" value={stats.total_sessions} n="sessions" />
          <StatCard icon={MousePointerClick} label="Affiliate clicks" value={stats.total_clicks} n="clicks" />
          <StatCard icon={Star} label="Avg. value score" value={`${stats.avg_value_score}/10`} n="score" />
        </div>

        {/* Decision-engine stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard icon={ShieldCheck} label="Avg. confidence" value={`${stats.avg_confidence ?? 0}%`} n="confidence" />
          <StatCard icon={ThumbsUp} label="Helpful %" value={`${stats.approval_percent ?? 0}%`} n="approval" />
          <StatCard icon={Share2} label="Share rate" value={`${stats.share_rate ?? 0}%`} n="share-rate" />
          <StatCard icon={Award} label="Top verdict" value={stats.verdict_distribution?.[0]?.type ? stats.verdict_distribution[0].type.replace(/_/g, " ") : "—"} n="verdict" />
        </div>

        {/* QA flag counts */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard icon={ShoppingBag} label="Purchase rate" value={`${stats.purchase_rate ?? 0}%`} n="purchase-rate" />
          <StatCard icon={AlertTriangle} label="Failed" value={stats.flag_counts?.failed ?? 0} n="failed" />
          <StatCard icon={AlertTriangle} label="Low confidence" value={stats.flag_counts?.low_confidence ?? 0} n="low-conf" />
          <StatCard icon={AlertTriangle} label="Missing category" value={stats.flag_counts?.missing_category ?? 0} n="no-cat" />
        </div>

        {/* Charts row */}
        <div className="grid lg:grid-cols-3 gap-4 mb-6">
          <div className="lg:col-span-2 bg-card border border-border rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4 text-emerald-500" />
              <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Searches · last 7 days</span>
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats.growth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} allowDecimals={false} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                  <Line type="monotone" dataKey="count" stroke="#10B981" strokeWidth={2.5} dot={{ fill: "#10B981" }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-card border border-border rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Tag className="w-4 h-4 text-emerald-500" />
              <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Top categories</span>
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.top_categories.slice(0, 6)} layout="vertical">
                  <XAxis type="number" stroke="hsl(var(--muted-foreground))" fontSize={10} allowDecimals={false} />
                  <YAxis dataKey="name" type="category" stroke="hsl(var(--muted-foreground))" fontSize={10} width={90} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                  <Bar dataKey="count" fill="#10B981" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Lists row */}
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div className="bg-card border border-border rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <Boxes className="w-4 h-4 text-emerald-500" />
              <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Most recommended products</span>
            </div>
            <div className="divide-y divide-border">
              {stats.top_products.length === 0 && <div className="text-sm text-muted-foreground py-3">No data yet.</div>}
              {stats.top_products.map((p, i) => (
                <div key={i} className="flex items-center justify-between py-2.5">
                  <span className="text-sm truncate pr-3">{p.name}</span>
                  <span className="text-xs font-semibold text-emerald-500">{p.count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-card border border-border rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <Award className="w-4 h-4 text-emerald-500" />
              <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Most helpful recommendations</span>
            </div>
            <div className="divide-y divide-border">
              {(stats.best_recommendations || []).length === 0 && (
                <div className="text-sm text-muted-foreground py-3">No votes yet.</div>
              )}
              {(stats.best_recommendations || []).map((p, i) => (
                <div key={i} className="flex items-center justify-between py-2.5">
                  <span className="text-sm truncate pr-3">{p.name}</span>
                  <span className="text-xs">
                    <span className="text-emerald-500 font-semibold">{p.helpful}</span>
                    <span className="text-muted-foreground"> / </span>
                    <span className="text-red-400 font-semibold">{p.unhelpful}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <div className="bg-card border border-border rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <MousePointerClick className="w-4 h-4 text-emerald-500" />
              <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Affiliate clicks by retailer</span>
            </div>
            <div className="divide-y divide-border">
              {stats.top_retailers.length === 0 && <div className="text-sm text-muted-foreground py-3">No clicks yet.</div>}
              {stats.top_retailers.map((r, i) => (
                <div key={i} className="flex items-center justify-between py-2.5">
                  <span className="text-sm">{r.name}</span>
                  <span className="text-xs font-semibold text-emerald-500">{r.count}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-card border border-border rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <Award className="w-4 h-4 text-emerald-500" />
              <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Verdict distribution</span>
            </div>
            <div className="divide-y divide-border">
              {(stats.verdict_distribution || []).length === 0 && (
                <div className="text-sm text-muted-foreground py-3">No verdicts yet.</div>
              )}
              {(stats.verdict_distribution || []).map((v, i) => (
                <div key={i} className="flex items-center justify-between py-2.5">
                  <span className="text-sm capitalize">{(v.type || "").replace(/_/g, " ")}</span>
                  <span className="text-xs font-semibold text-emerald-500">{v.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Recent searches */}
        <div className="bg-card border border-border rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <History className="w-4 h-4 text-emerald-500" />
            <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Recent searches</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border">
                  <th className="text-left py-2 font-semibold">Query</th>
                  <th className="text-left py-2 font-semibold">Best product</th>
                  <th className="text-left py-2 font-semibold">Verdict</th>
                  <th className="text-left py-2 font-semibold">Confidence</th>
                  <th className="text-left py-2 font-semibold">Score</th>
                  <th className="text-left py-2 font-semibold">👍/👎</th>
                  <th className="text-left py-2 font-semibold">Time</th>
                </tr>
              </thead>
              <tbody>
                {searches.slice(0, 25).map((s) => (
                  <tr key={s.id} className="border-b border-border/50">
                    <td className="py-3 pr-4 truncate max-w-[220px]" title={s.query}>{s.query}</td>
                    <td className="py-3 pr-4 text-muted-foreground truncate max-w-[180px]">{s.best_product || "—"}</td>
                    <td className="py-3 pr-4 text-xs capitalize">{(s.verdict_type || "—").replace(/_/g, " ")}</td>
                    <td className="py-3 pr-4 text-xs text-muted-foreground">{s.confidence != null ? `${s.confidence}%` : "—"}</td>
                    <td className="py-3 pr-4 text-emerald-500 font-semibold">{s.value_score ?? "—"}</td>
                    <td className="py-3 pr-4 text-xs">
                      <span className="text-emerald-500 font-semibold">{s.helpful_votes ?? 0}</span>
                      <span className="text-muted-foreground"> / </span>
                      <span className="text-red-400 font-semibold">{s.unhelpful_votes ?? 0}</span>
                    </td>
                    <td className="py-3 pr-4 text-muted-foreground text-xs">{new Date(s.created_at).toLocaleString("en-GB")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {searches.length === 0 && <div className="text-sm text-muted-foreground py-4 text-center">No searches yet.</div>}
          </div>
        </div>

        {/* Danger zone — dev/test data reset */}
        <div
          data-testid="danger-zone"
          className="mt-8 bg-card border border-red-500/30 rounded-2xl p-5"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1.5">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <span className="text-xs uppercase tracking-widest text-red-300 font-semibold">Danger zone</span>
              </div>
              <h3 className="text-base font-semibold mb-1">Reset development metrics</h3>
              <p className="text-sm text-muted-foreground max-w-2xl leading-relaxed">
                Clears all helpful / unhelpful votes, share counts, purchase counts and admin feedback generated
                during development & testing. QA notes and audit ratings are preserved.
              </p>
            </div>
            <button
              onClick={() => { setResetOpen(true); setResetMsg(""); setResetConfirm(""); }}
              data-testid="reset-metrics-btn"
              className="flex items-center gap-2 text-xs px-4 py-2.5 rounded-lg border border-red-500/40 text-red-400 hover:bg-red-500/10 transition font-semibold"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reset dev metrics
            </button>
          </div>
          {resetMsg && (
            <div
              data-testid="reset-result"
              className={`mt-4 text-xs px-3 py-2 rounded-lg border ${
                resetMsg.startsWith("Failed")
                  ? "border-red-500/40 text-red-300 bg-red-500/5"
                  : "border-emerald-500/40 text-emerald-300 bg-emerald-500/5"
              }`}
            >
              {resetMsg}
            </div>
          )}
        </div>
      </main>

      {/* Reset confirmation modal */}
      {resetOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="reset-modal">
          <button
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => !resetBusy && setResetOpen(false)}
            aria-label="Close"
          />
          <div className="relative w-full max-w-md bg-card border border-border rounded-2xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                <h2 className="font-display font-bold text-lg">Confirm reset</h2>
              </div>
              <button
                onClick={() => !resetBusy && setResetOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-secondary"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
              This will permanently delete all votes, share counts, purchase counts and admin feedback across the
              entire database. <strong className="text-foreground">This cannot be undone.</strong>
            </p>
            <label className="flex items-center gap-2 text-sm mb-4 cursor-pointer">
              <input
                type="checkbox"
                checked={includeClicks}
                onChange={(e) => setIncludeClicks(e.target.checked)}
                className="accent-emerald-500"
                data-testid="reset-include-clicks"
              />
              Also delete affiliate click history
            </label>
            <label className="block text-xs text-muted-foreground mb-1.5 uppercase tracking-widest font-semibold">
              Type <span className="text-red-400 font-mono">RESET</span> to confirm
            </label>
            <input
              value={resetConfirm}
              onChange={(e) => setResetConfirm(e.target.value)}
              data-testid="reset-confirm-input"
              placeholder="RESET"
              className="w-full bg-background border border-border rounded-lg px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-red-500/60 mb-4"
            />
            <div className="flex gap-2">
              <button
                onClick={() => setResetOpen(false)}
                disabled={resetBusy}
                className="flex-1 text-sm px-4 py-2.5 rounded-lg border border-border hover:bg-secondary transition disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                onClick={runReset}
                disabled={resetBusy || resetConfirm !== "RESET"}
                data-testid="reset-confirm-btn"
                className="flex-1 text-sm font-semibold px-4 py-2.5 rounded-lg bg-red-500 text-white hover:bg-red-400 transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {resetBusy ? "Resetting…" : "Reset everything"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
